<template>
  <section class="drawer" id="form-items-drawer">
    <div class="wrapper">
      <header>
        <h2>
          {{
            action === "add"
              ? "Elementos del formulario"
              : `Propiedades de ${getNameByType(input?.type)}`
          }}
        </h2>

        <a-button type="text" class="button" @click="handleClose">
          <CloseOutlined />
        </a-button>
      </header>

      <CustomScrollbar height="calc(100% - 45px)" auto>
        <div class="body">
          <ElementPicker v-if="action === 'add'" ref="pickerRef" :form="form" />

          <component
            v-if="action === 'edit'"
            ref="settingsRef"
            :is="componentToUse"
            :input="cloneDeep(input)"
          />
        </div>
      </CustomScrollbar>
    </div>

    <footer>
      <a-button @click="handleClose">Ocultar</a-button>
      <a-button
        type="primary"
        id="add-form-item"
        @click="handleSave"
        :disabled="action === 'add' ? false : isSaveDisabled"
      >
        {{ action === "add" ? "Agregar elemento" : "Guardar elemento" }}
      </a-button>
    </footer>
  </section>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { CloseOutlined } from "@ant-design/icons-vue";
import { isEqual, cloneDeep } from "lodash";

import {
  DrawerAction,
  ElementType,
  FormElement,
  FormElementOption,
  FormStateElementMap,
} from "@/@types/entities/forms";
import { getNameByType } from "@/app/entities/utils/forms";

import ElementPicker from "@/app/entities/components/picker/forms/ElementPicker.vue";
import HeaderForm from "@/app/entities/components/drawers/forms/settings/Header.vue";
import FileForm from "@/app/entities/components/drawers/forms/settings/File.vue";
import ShortText from "@/app/entities/components/drawers/forms/settings/ShortText.vue";
import LongText from "@/app/entities/components/drawers/forms/settings/LongText.vue";
import SingleOption from "@/app/entities/components/drawers/forms/settings/SingleOption.vue";
import MultipleOptions from "@/app/entities/components/drawers/forms/settings/MultipleOptions.vue";
import Dropdown from "@/app/entities/components/drawers/forms/settings/Dropdown.vue";
import Date from "@/app/entities/components/drawers/forms/settings/Date.vue";
import Hour from "@/app/entities/components/drawers/forms/settings/Hour.vue";
import CustomScrollbar from "@/app/shared/components/organisms/CustomScrollbar.vue";

const props = defineProps<{
  action: DrawerAction;
  input?: FormElement;
  form: FormElement[];
}>();

const emit = defineEmits<{
  (e: "add", element: FormElementOption | undefined | null): void;
  (e: "edit", formState: FormStateElementMap[ElementType]): void;
  (e: "close"): void;
}>();

const FormByType: Record<ElementType, unknown> = {
  header: HeaderForm,
  file: FileForm,
  "short-text": ShortText,
  "long-text": LongText,
  "single-option": SingleOption,
  "multiple-options": MultipleOptions,
  dropdown: Dropdown,
  date: Date,
  hour: Hour,
  pdp: null,
  reminder: null,
};

const componentToUse = computed(() => (props.input ? FormByType[props.input.type] : null));

const pickerRef = ref<InstanceType<typeof ElementPicker> | undefined>(undefined);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const settingsRef = ref<any>(undefined);

const isSaveDisabled = computed(() => {
  const form = settingsRef.value?.formState;

  for (const key in form) {
    if (typeof form[key] !== "object") {
      if (form[key] !== props.input?.[key as keyof FormElement]) return false;
    } else {
      if (!isEqual(form[key], props.input?.[key as keyof FormElement])) return false;
    }
  }

  return true;
});

const handleClose = () => {
  emit("close");
};

const handleSave = () => {
  if (props.action === "add") emit("add", pickerRef.value?.selected);
  if (props.action === "edit") {
    console.log(settingsRef.value?.formRef);

    settingsRef.value?.formRef.validateFields().then(() => {
      emit("edit", settingsRef.value?.formState);
      handleClose();
    });
  }
};
</script>

<style scoped lang="sass">
.drawer
  display: flex
  flex: 1 1 auto
  max-width: 650px
  flex-direction: column
  justify-content: space-between
  background: #FFFFFF
  box-shadow: -4px 0px 10px rgba(0, 0, 0, 0.15)
  border-radius: 4px
  overflow: hidden
  // height: calc(100vh - 118px)
  height: calc(100vh - 195px)

.wrapper
  height: calc(100% - 57px)

h2
  color: $gray-9
  font-weight: 600
  font-size: 16px
  margin: 0
  line-height: 24px

.button
  padding: 0 5px
  border-radius: 4px
  height: 24px
  font-size: 16px

header
  border-bottom: 1px solid $gray-4
  padding: 10px 20px
  display: flex
  align-items: center
  justify-content: space-between

.body
  padding: 16px 24px

footer
  border-top: 1px solid $gray-4
  padding: 12px 16px
  display: flex
  align-items: center
  justify-content: flex-end
  gap: 8px
</style>
